/* istanbul ignore file */
import { registerDefaultsForModule, ComposedTheme } from '@glu/theming';

/**
 * Create the defaults for the buttons module
 *
 * @param {boolean} legacy - legacy theme flag from glu theming
 * @param {Object} palette - palette hash from glu theming
 */

export const themeDefaults = ({ legacy, palette, spacing, typography }: ComposedTheme) => {
  const disabledColor = '#A1C2E1';
  return {
    ...(legacy
      ? {
          cursor: 'pointer',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          outlineOffset: -2,
          breakpoint: 768,
          primary: {
            backgroundColor: palette.primary.normal,
            borderColor: palette.primary.normal,
            focusColor: palette.primary.veryDark,
            hoverColor: palette.primary.dark,
            textColor: palette.text.light,
            disabledBackground: disabledColor,
            disabledBorder: disabledColor,
            disabledColor: palette.text.light,
            spinnerBackground: disabledColor,
            spinnerBorder: disabledColor,
            spinnerColor: palette.text.light,
          },
          secondary: {
            backgroundColor: palette.background,
            borderColor: palette.accent,
            focusColor: '#E6E6E6',
            focusBorderColor: '#ADADAD',
            hoverColor: '#EDEDED',
            textColor: palette.primary.normal,
            disabledBackground: palette.background,
            disabledBorder: '#E7E7E7',
            disabledColor,
            spinnerBackground: palette.background,
            spinnerBorder: '#E7E7E7',
            spinnerColor: disabledColor,
          },
          tertiary: {
            backgroundColor: 'transparent',
            textDecoration: 'none',
            focusColor: 'transparent',
            focusTextDecoration: 'underline',
            hoverColor: 'transparent',
            hoverTextDecoration: 'underline',
            textColor: palette.primary.normal,
            disabledColor,
          },
          normal: {
            borderRadius: 4,
            fontSize: 14,
            spinnerSize: 20,
            padding: [9, 12],
          },
          small: {
            borderRadius: 3,
            fontSize: 12,
            spinnerSize: 14,
            padding: [8, 10],
          },
          extraSmall: {
            borderRadius: 3,
            fontSize: 12,
            spinnerSize: 10,
            padding: [4, 5],
          },

          focusColor: palette.focus.normal,

          buttonToggleGroup: {
            borderRadius: 30,
            focusBorderColor: palette.primary.normal,
            focusBorderWidth: 1,
            focusPadding: '5px 24px',
            fontFamily: typography.h3.fontFamily,
            fontSize: 16,
            lineHeight: 1,
            toggleBackground: 'transparent',
            togglePadding: '6px 25px',
            textSize: '14px',
            selectedToggleBackground: palette.primary.normal,
            selectedFontColor: palette.text.light,
            fontColor: palette.primary.normal,
            toggleBorderColor: palette.primary.normal,
          },

          menuButton: {
            borderRadius: 30,
            focusBorderColor: palette.focus.normal,
            focusBorderWidth: 2,
            focusPadding: '5px 24px',
            fontFamily: typography.h3.fontFamily,
            fontSize: 18,
            lineHeight: 1,
            background: 'transparent',
            backgroundHover: palette.highlightBackground,
            padding: '3px 0px',
            textSize: '14px',
            dropdownToggleBorder: palette.grey.G400,
            dropdownOffset: 31, // deprecated - unused
            dropdownBackground: palette.background,
            dropdownRadius: 5,
            dropdownMenuHover: palette.highlightBackground,
            fontColor: palette.primary.normal,
            borderColor: palette.primary.normal,
            primary: {
              background: palette.primary.normal,
              fontColor: palette.text.light,
              dropdownToggleBorder: palette.background,
              backgroundHover: palette.primary.dark,
            },
            tertiary: {
              background: 'transparent',
              fontColor: palette.primary.normal,
              dropdownToggleBorder: palette.background,
              backgroundHover: 'transparent',
            },
            small: {
              fontSize: 16,
              borderRadius: 15,
            },
            extraSmall: {
              fontSize: 14,
              borderRadius: 11,
            },
          },

          block: {
            backgroundColor: palette.primary.normal,
            textColor: palette.text.light,
            disabledBackgroundColor: palette.primary.disabled,
            disabledTextColor: palette.text.light,
            hoverBackgroundColor: palette.primary.dark,
            focusBackgroundColor: palette.primary.dark,
            borderColor: palette.primary.dark,
            disabledBorderColor: palette.primary.disabled,
          },
          icon: {
            iconMargin: spacing.unit,
          },
          prominentColor: palette.info,
        }
      : {
          // @theming labelText: Button cursor, type: string
          cursor: 'pointer',
          // @theming labelText: Font family, type: fontFamily
          fontFamily: '"Roboto Condensed", Roboto, Helvetica, Arial, sans-serif',
          // @theming labelText: Outline offset, type: string
          outlineOffset: undefined,
          // @theming labelText: Page actions breakpoint, type: pixel
          breakpoint: 768,
          primary: {
            // @theming labelText: Primary background color, type: color
            backgroundColor: palette.colors.lochmara,
            // @theming labelText: Primary border color, type: color
            borderColor: palette.colors.lochmara,
            // @theming labelText: Primary focus color, type: color
            focusColor: palette.primary.dark,
            // @theming labelText: Primary hover color, type: color
            hoverColor: palette.primary.dark,
            // @theming labelText: Primary text color, type: color, backgroundColor: buttons.primary.backgroundColor
            textColor: palette.text.light,
            // @theming labelText: Focus box shadow, type: string
            outlineReplacement: `0 0 3px 2px ${palette.focus.normal}`,
            // @theming labelText: Primary disabled background color, type: color
            disabledBackground: palette.primary.disabled,
            // @theming labelText: Primary disabled border color, type: color
            disabledBorder: palette.primary.disabled,
            // @theming labelText: Primary disabled text color, type: color, backgroundColor: buttons.primary.disabledBackground
            disabledColor: palette.text.light,
            // @theming labelText: Primary spinner color, type: color
            spinnerColor: palette.text.light,
            // @theming labelText: Primary text transform, type: string
            textTransform: undefined,
            // @theming labelText: Primary font weight, type: string
            fontWeight: undefined,
          },
          secondary: {
            // @theming labelText: Secondary background color, type: color
            backgroundColor: palette.background,
            // @theming labelText: Secondary border color, type: color
            borderColor: palette.colors.lochmara,
            // @theming labelText: Secondary focus color, type: color
            focusColor: palette.highlightBackground,
            // @theming labelText: Secondary focus border color, type: color
            focusBorderColor: palette.colors.lochmara,
            // @theming labelText: Secondary hover color, type: color
            hoverColor: palette.highlightBackground,
            // @theming labelText: Secondary text color, type: color, backgroundColor: buttons.secondary.backgroundColor
            textColor: palette.colors.lochmara,
            // @theming labelText: Secondary disabled background color, type: color
            disabledBackground: palette.background,
            // @theming labelText: Secondary disabled border color, type: color
            disabledBorder: palette.primary.disabled,
            // @theming labelText: Secondary disabled text color, type: color, backgroundColor: buttons.secondary.disabledBackground
            disabledColor: palette.primary.disabled,
            // @theming labelText: Secondary spinner color, type: color
            spinnerColor: palette.colors.lochmara,
            // @theming labelText: Secondary text transform, type: string
            textTransform: undefined,
            // @theming labelText: Secondary font weight, type: string
            fontWeight: undefined,
          },
          tertiary: {
            // @theming labelText: Tertiary background color, type: color
            backgroundColor: 'transparent',
            // @theming labelText: Tertiary text decoration, type: string
            textDecoration: 'none',
            // @theming labelText: Tertiary focus background color, type: color
            focusColor: 'transparent',
            // @theming labelText: Tertiary focus text decoration, type: string
            focusTextDecoration: 'underline',
            // @theming labelText: Tertiary hover text color, type: color
            hoverColor: 'transparent',
            // @theming labelText: Tertiary hover text decoration, type: string
            hoverTextDecoration: 'underline',
            // @theming labelText: Tertiary text color, type: color, backgroundColor: buttons.tertiary.backgroundColor
            textColor: palette.colors.lochmara,
            // @theming labelText: Tertiary padding, type: pixel
            padding: 0,
            // @theming labelText: Tertiary disabled text color, type: color
            disabledColor: palette.primary.disabled,
          },
          normal: {
            // @theming labelText: Normal border radius, type: pixel
            borderRadius: 40,
            // @theming labelText: Normal font size, type: pixel
            fontSize: 18,
            // @theming labelText: Normal spinner size, type: pixel
            spinnerSize: 24,
            // @theming labelText: Normal padding, type: pixel
            padding: [9, 25],
          },
          small: {
            // @theming labelText: Small border radius, type: pixel
            borderRadius: 40,
            // @theming labelText: Small font size, type: pixel
            fontSize: 16,
            // @theming labelText: Small spinner size, type: pixel
            spinnerSize: 18,
            // @theming labelText: Small padding, type: pixel
            padding: [5, 25],
          },
          extraSmall: {
            // @theming labelText: Extra small border radius, type: pixel
            borderRadius: 40,
            // @theming labelText: Extra small font size, type: pixel
            fontSize: 14,
            // @theming labelText: Extra small spinner size, type: pixel
            spinnerSize: 14,
            // @theming labelText: Extra small padding, type: pixel
            padding: [2, 20],
          },
          // @theming labelText: Focus outline color, type: color
          focusColor: palette.focus.normal,

          buttonToggleGroup: {
            // @theming labelText: Button toggle text color, type: color
            fontColor: palette.colors.lochmara,
            // @theming labelText: Button toggle font size, type: pixel
            fontSize: 16,
            // @theming labelText: Button toggle font family, type: fontFamily
            fontFamily: typography.h3.fontFamily,
            // @theming labelText: Button toggle border radius, type: string
            borderRadius: 30,
            // @theming labelText: Button toggle background color, type: color
            toggleBackground: 'transparent',
            // @theming labelText: Button toggle line height, type: string
            lineHeight: 1,
            // @theming labelText: Button toggle padding, type: pixel
            togglePadding: [6, 25],
            // @theming labelText: Button toggle hover background color, type: color
            selectedToggleBackground: palette.primary.normal,
            // @theming labelText: Button toggle hover/selected font color
            selectedFontColor: palette.text.light,
            // @theming labelText: Button toggle border color, type: color
            toggleBorderColor: palette.form.border,
            // @theming labelText: Button toggle disabled background color, type: color
            disabledBackground: palette.background,
            // @theming labelText: Button toggle disabled border color, type: color
            disabledBorder: palette.primary.disabled,
            // @theming labelText: Button toggle disabled text color, type: color, backgroundColor: buttons.buttonToggleGroup.disabledBackground
            disabledColor: palette.primary.disabled,
            // @theming labelText: Button toggle disabled fill color, type: color, backgroundColor: buttons.buttonToggleGroup.disabledBackground
            disabledFill: palette.primary.disabled,
          },

          menuButton: {
            // @theming labelText: Menu button border radius, type: pixel
            borderRadius: 20,
            // @Unused property - to be removed in GLU-7487
            focusBorderColor: palette.focus.normal,
            // @Unused property - to be removed in GLU-7487
            focusBorderWidth: 2,
            // @Unused property - to be removed in GLU-7487
            focusPadding: '5px 24px',
            // @theming labelText: Menu button font family, type: fontFamily
            fontFamily: typography.h3.fontFamily,
            // @theming labelText: Menu button font size, type: pixel
            fontSize: 18,
            // @theming labelText: Menu button line height, type: string
            lineHeight: 1,
            // @theming labelText: Menu button background color, type: color
            background: 'transparent',
            // @theming labelText: Menu button hover background color, type: color
            backgroundHover: palette.highlightBackground,
            // @theming labelText: Menu button padding, type: pixel
            padding: '3px 0px',
            // @theming labelText: Menu button font size, type: pixel
            textSize: '14px',
            // @theming labelText: Menu button toggle border color, type: color
            dropdownToggleBorder: palette.grey.G400,
            // @Unused property - to be removed in GLU-7487
            dropdownOffset: 31, // deprecated - unused
            // @theming labelText: Menu button dropdown background color, type: color
            dropdownBackground: palette.background,
            // @theming labelText: Menu button dropdown border radius, type: pixel
            dropdownRadius: 5,
            // @theming labelText: Menu button dropdown background hover color, type: color
            dropdownMenuHover: palette.highlightBackground,
            // @theming labelText: Menu button dropdown text color, type: color, backgroundColor: buttons.menuButton.background
            fontColor: palette.colors.lochmara,
            // @theming labelText: Menu button dropdown border color, type: color
            borderColor: palette.colors.lochmara,
            // @theming labelText: Menu button dropdown action text color, type: color, backgroundColor: buttons.menuButton.dropdownBackground
            dropdownActionColor: palette.colors.mineShaft,
            primary: {
              // @theming labelText: Menu button primary background color, type: color
              background: palette.primary.normal,
              // @theming labelText: Menu button primary text color, type: color, backgroundColor: buttons.menuButton.primary.background
              fontColor: palette.text.light,
              // @theming labelText: Menu button primary border color, type: color
              dropdownToggleBorder: palette.background,
              // @theming labelText: Menu button primary background hover color, type: color
              backgroundHover: palette.primary.dark,
            },
            tertiary: {
              // @theming labelText: Menu button tertiary background color, type: color
              background: 'transparent',
              // @theming labelText: Menu button tertiary text color, type: color, backgroundColor: buttons.menuButton.tertiary.background
              fontColor: palette.primary.normal,
              // @theming labelText: Menu button tertiary border color, type: color
              dropdownToggleBorder: palette.background,
              // @theming labelText: Menu button tertiary background hover color, type: color
              backgroundHover: 'transparent',
            },
            small: {
              // @theming labelText: Menu button small font size, type: pixel
              fontSize: 16,
              // @theming labelText: Menu button small border radius, type: pixel
              borderRadius: 15,
            },
            extraSmall: {
              // @theming labelText: Menu button extra small font size, type: pixel
              fontSize: 14,
              // @theming labelText: Menu button extra small border radius, type: pixel
              borderRadius: 11,
            },
          },
          block: {
            // @theming labelText: Block background color, type: color
            backgroundColor: palette.primary.normal,
            // @theming labelText: Block text color, type: color, backgroundColor: buttons.block.backgroundColor
            textColor: palette.text.light,
            // @theming labelText: Block disabled background color, type: color
            disabledBackgroundColor: palette.primary.disabled,
            // @theming labelText: Block disabled text color, type: color, backgroundColor: buttons.block.disabledBackgroundColor
            disabledTextColor: palette.text.light,
            // @theming labelText: Block hover background color, type: color
            hoverBackgroundColor: palette.primary.dark,
            // @theming labelText: Block focus background color, type: color
            focusBackgroundColor: palette.primary.dark,
            // @theming labelText: Block border color, type: color
            borderColor: palette.primary.dark,
            // @theming labelText: Block disabled border color, type: color
            disabledBorderColor: palette.primary.disabled,
          },
          icon: {
            // @theming labelText: Icon margin to text, type: pixel
            iconMargin: spacing.unit,
          },
          // @theming labelText: Prominent text color, type: color
          prominentColor: palette.info,
        }),
  };
};

registerDefaultsForModule('buttons', themeDefaults);

export default () => {
  registerDefaultsForModule('buttons', themeDefaults);
};
